import { Moment } from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import FlightLookupTile from './FlightLookupTile'
import {
  flightConfirmationCodeConfig,
  formOptions,
} from '../../Common/Fields/config'
import Input from '../../Common/Fields/RHFInput'
import CreateFlight from '../../Create/CreateFlight'

import {
  addToTripClickEvent,
  addToTripSaveErrorEvent,
  addToTripSaveEvent,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextLoadingBox from 'components/Luxkit/Typography/TextLoadingBox'
import Group from 'components/utils/Group'
import {
  ISO_DATE_FORMAT,
  SHORT_TIME_FORMAT_24_HOUR,
  DMY_CASUAL_FORMAT,
} from 'constants/dateFormats'
import { toggleHas } from 'lib/array/setUtils'
import { useAirports, useCreateTripItemBatch } from 'tripPlanner/hooks/api'
import { useGetFlightSchedules } from 'tripPlanner/hooks/api/misc'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { FlightSchedule } from 'tripPlanner/types/common'
import { CreateFlightRequestBody } from 'tripPlanner/utils/itemConversions/common'

interface Props {
  flightCode: string
  departureDate: Moment
  tripId: string
}

type FormValues = { confirmationCode?: string }

const EMPTY_FLIGHT_ARRAY: Array<FlightSchedule> = []

export default function FlightLookupListing({
  flightCode,
  departureDate,
  tripId,
}: Props) {
  const [selectedKeys, setSelectedKeys] = useState(new Set<string>())
  const toggleKey = useCallback(
    (id: string) =>
      setSelectedKeys((selectedKeys) => {
        const newSelections = new Set([...selectedKeys])
        toggleHas(newSelections, id)
        return newSelections
      }),
    [],
  )
  const { closeModal, goBack } = useModalCallbacks()
  const openModal = useOpenModal()

  const methods = useForm<FormValues>({
    ...formOptions,
    defaultValues: {},
  })
  const { getValues, handleSubmit, register } = methods

  const { mutate: createTripItems, isLoading: isCreatingFlights } =
    useCreateTripItemBatch({
      onSuccess: () => {
        fireInteractionEvent(addToTripSaveEvent('flights_custom_lookup'))
        closeModal()
      },
      onError: () => {
        fireInteractionEvent(addToTripSaveErrorEvent('flights_custom_lookup'))
      },
    })

  const {
    data: flights,
    isLoading,
    isError,
  } = useGetFlightSchedules(
    { departureDate, flightCode },
    {
      onSuccess: (flights) => {
        setSelectedKeys(new Set(flights.map((flight) => flight.key)))
      },
    },
  )

  const selectedFlights = useMemo(
    () =>
      (flights ?? EMPTY_FLIGHT_ARRAY).filter((flight) =>
        selectedKeys.has(flight.key),
      ),
    [flights, selectedKeys],
  )
  const hasSelectedFlight = selectedFlights.length > 0

  const airportCodes = useMemo(() => {
    if (!flights) {
      return []
    }
    const codes = flights.flatMap((f) => [
      f.departureAirportCode,
      f.arrivalAirportCode,
    ])
    return [...new Set(codes)].sort()
  }, [flights])

  const airportResults = useAirports({ codes: airportCodes })

  const airportLoaded = (airportCode) =>
    airportResults.some((r) => r.data?.airportCode === airportCode)

  const hasAllRequiredAirportData = selectedFlights.every(
    (flight) =>
      airportLoaded(flight.departureAirportCode) &&
      airportLoaded(flight.arrivalAirportCode),
  )

  const airportsLoading =
    !hasAllRequiredAirportData && airportResults.some((r) => r.isLoading)

  const saveFlight: SubmitHandler<FormValues> = useCallback(
    (values) => {
      // This will only fire if there is a selectedFlight
      // Yes there's probably better ways of doing it, crunch time!
      if (selectedFlights.length > 0 && hasAllRequiredAirportData) {
        const tripItems: Array<CreateFlightRequestBody> = selectedFlights.map(
          (flight) => {
            // We know these will exist here due the the `hasAllRequiredAirportData` check
            const departureAirport = airportResults.find(
              (r) => r.data?.airportCode === flight.departureAirportCode,
            )!.data!
            const arrivalAirport = airportResults.find(
              (r) => r.data?.airportCode === flight.arrivalAirportCode,
            )!.data!

            return {
              type: 'FLIGHT',
              airlineName: flight.airlineName,
              flightNumber: flightCode,
              destinationAirportCode: flight.arrivalAirportCode,
              originAirportCode: flight.departureAirportCode,
              sourceType: 'NA',
              startDate: flight.departureDate.format(ISO_DATE_FORMAT),
              startTime: flight.departureTime.format(SHORT_TIME_FORMAT_24_HOUR),
              endDate: flight.arrivalDate.format(ISO_DATE_FORMAT),
              endTime: flight.arrivalTime.format(SHORT_TIME_FORMAT_24_HOUR),
              startPlace: {
                name: departureAirport.airportName || '',
                point: {
                  lat: departureAirport.lt!,
                  lng: departureAirport.lg!,
                },
                type: 'GEO',
              },
              endPlace: {
                name: arrivalAirport.airportName || '',
                point: {
                  lat: arrivalAirport.lt!,
                  lng: arrivalAirport.lg!,
                },
                type: 'GEO',
              },
              confirmationCode: values.confirmationCode,
              isBooked: true,
            }
          },
        )

        createTripItems({ tripId, tripItems })
      }
    },
    [
      selectedFlights,
      hasAllRequiredAirportData,
      createTripItems,
      tripId,
      airportResults,
      flightCode,
    ],
  )

  const createCustomFlight = useCallback(() => {
    fireInteractionEvent(
      addToTripClickEvent('modal_flights_custom_manual', 'postlookup'),
    )
    if (!flights) {
      openModal(
        CreateFlight,
        {
          tripId,
          initialData: {
            flightNumber: flightCode,
            confirmationCode: getValues('confirmationCode'),
            startDate: departureDate.format(ISO_DATE_FORMAT),
            startPlace: {
              name: '',
              type: 'CUSTOM' as const,
            },
          },
        },
        {},
      )
      return
    } else if (selectedFlights.length !== 1 || !hasAllRequiredAirportData) {
      openModal(
        CreateFlight,
        {
          tripId,
          initialData: {
            flightNumber: flightCode,
            airlineName: flights ? flights?.[0]?.airlineName : '',
            confirmationCode: getValues('confirmationCode'),
            startDate: departureDate.format(ISO_DATE_FORMAT),
            startPlace: {
              name: '',
              type: 'CUSTOM' as const,
            },
          },
        },
        {},
      )
      return
    } else {
      const flight = selectedFlights[0]
      // We know these will exist here due the the `hasAllRequiredAirportData` check
      const departureAirport = airportResults.find(
        (r) => r.data?.airportCode === flight.departureAirportCode,
      )!.data!
      const arrivalAirport = airportResults.find(
        (r) => r.data?.airportCode === flight.arrivalAirportCode,
      )!.data!

      openModal(
        CreateFlight,
        {
          tripId,
          initialData: {
            originAirportCode: flight.departureAirportCode,
            destinationAirportCode: flight.arrivalAirportCode,
            startDate: flight.departureDate.format(ISO_DATE_FORMAT),
            startTime: flight.departureTime.format(SHORT_TIME_FORMAT_24_HOUR),
            endDate: flight.arrivalDate.format(ISO_DATE_FORMAT),
            endTime: flight.arrivalTime.format(SHORT_TIME_FORMAT_24_HOUR),
            startPlace: {
              name: departureAirport.airportName || '',
              point: {
                lat: departureAirport.lt!,
                lng: departureAirport.lg!,
              },
              type: 'GEO' as const,
            },
            endPlace: {
              name: arrivalAirport?.airportName || '',
              point: {
                lat: arrivalAirport.lt!,
                lng: arrivalAirport.lg!,
              },
              type: 'GEO' as const,
            },
            flightNumber: flight.airlineCode + flight.flightNumber?.toString(),
            airlineName: flight.airlineName,
            isEndDateSame:
              flight.arrivalDate.toISOString() ===
              flight.departureDate.toISOString(),
            confirmationCode: getValues('confirmationCode'),
          },
        },
        {},
      )
    }
  }, [
    flights,
    selectedFlights,
    hasAllRequiredAirportData,
    openModal,
    tripId,
    flightCode,
    getValues,
    departureDate,
    airportResults,
  ])

  const primaryDisabled =
    airportsLoading || isCreatingFlights || !hasSelectedFlight

  let modalHeaderTitle: string = 'Flight'
  if (isLoading) {
    modalHeaderTitle = 'Finding flight...'
  } else if (isError) {
    modalHeaderTitle = 'Flight not found'
  } else if (flights.length > 0) {
    modalHeaderTitle = 'Flight details'
  }

  return (
    <>
      <ModalHeader
        title={modalHeaderTitle}
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={20}>
            <BodyText variant="large">
              <b>{flightCode}</b> departing on{' '}
              <b>{departureDate.format(DMY_CASUAL_FORMAT)}</b>
            </BodyText>

            {flights && flights.length > 0 && (
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(saveFlight)}>
                  <VerticalSpacer gap={16}>
                    <BodyText variant="large">
                      {flights.length > 1 && 'Are these your flights?'}
                      {flights.length === 1 && 'Is this your flight?'}
                    </BodyText>
                    {flights.map((flight) => (
                      <FlightLookupTile
                        key={flight.key}
                        flight={flight}
                        showSelect={flights.length > 1}
                        selected={selectedKeys.has(flight.key)}
                        onClick={toggleKey}
                      />
                    ))}
                    <Group
                      direction="horizontal"
                      horizontalAlign="stretch"
                      verticalAlign="center"
                      gap={16}
                    >
                      {/* Low priority feature, will follow up with this next sprint (26/10/2022) <Controller
                      name="cabinClass"
                      control={methods.control}
                      render={({ field }) => {
                        return (
                          <Select {...field} label="Type" required>
                            {SELECT_OPTIONS.map((opt) => (
                              <option
                                key={opt.value}
                                value={opt.value}
                                label={opt.label}
                              >
                                {opt.label}
                              </option>
                            ))}
                          </Select>
                        )
                      }}
                    /> */}
                      <Input
                        {...register('confirmationCode', {
                          ...flightConfirmationCodeConfig,
                        })}
                        label="Confirmation code"
                        placeholder="Confirmation code"
                      />
                    </Group>
                    <TextButton
                      kind="primary"
                      size="large"
                      fit="full-width"
                      type="submit"
                      disabled={primaryDisabled}
                    >
                      Save
                    </TextButton>
                    <TextButton
                      kind="tertiary"
                      size="large"
                      fit="full-width"
                      onClick={createCustomFlight}
                    >
                      or enter details manually
                    </TextButton>
                  </VerticalSpacer>
                </form>
              </FormProvider>
            )}
            {isLoading && (
              <TextLoadingBox typography="body-large" lines={2} width="100%" />
            )}
            {!isLoading && flights?.length === 0 && (
              <>
                <BodyText variant="large">
                  Unfortunately we couldn’t find any flights matching your
                  details.
                </BodyText>
                <VerticalSpacer gap={16}>
                  <TextButton
                    kind="primary"
                    size="large"
                    fit="full-width"
                    onClick={goBack}
                  >
                    Try another search
                  </TextButton>
                  <TextButton
                    kind="tertiary"
                    size="large"
                    fit="full-width"
                    onClick={createCustomFlight}
                  >
                    or enter details manually
                  </TextButton>
                </VerticalSpacer>
              </>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}
